import { z } from 'zod';
// ---- Primitivas ----------------------------------------------------------------
export const CATALOG_NAME_MAX = 80;
export const CATALOG_TAGS_MAX = 10;
export const ITEM_VARIANTS_MAX = 30;
/** $ 10.000.000 en centavos: tope razonable para evitar errores de tipeo. */
export const PRICE_CENTS_MAX = 1_000_000_000;
const idSchema = z.string().trim().min(1).max(64);
export const catalogNameSchema = z
    .string()
    .trim()
    .min(1, 'Ingresá un nombre')
    .max(CATALOG_NAME_MAX, `Máximo ${CATALOG_NAME_MAX} caracteres`);
const optionalText = (max) => z.string().trim().max(max, `Máximo ${max} caracteres`).nullable().optional();
export const priceCentsSchema = z
    .number('Ingresá un precio')
    .int('El precio tiene que ser un número entero de centavos')
    .min(0, 'El precio no puede ser negativo')
    .max(PRICE_CENTS_MAX, 'El precio es demasiado alto');
const optionalCostSchema = priceCentsSchema.nullable().optional();
export const imageUrlSchema = z.url('URL inválida').or(z.literal('')).nullable().optional();
export const tagsSchema = z
    .array(z.string().trim().min(1).max(30, 'Cada etiqueta puede tener hasta 30 caracteres'))
    .max(CATALOG_TAGS_MAX, `Máximo ${CATALOG_TAGS_MAX} etiquetas`);
const sortOrderSchema = z.number().int().min(0).max(1_000_000);
// ---- Categorías ----------------------------------------------------------------
export const createCategorySchema = z.object({
    name: catalogNameSchema,
    description: optionalText(300),
    imageUrl: imageUrlSchema,
    isActive: z.boolean().optional(),
    sortOrder: sortOrderSchema.optional(),
});
export const updateCategorySchema = createCategorySchema.partial();
// ---- Variantes -----------------------------------------------------------------
export const createVariantSchema = z.object({
    name: catalogNameSchema,
    priceCents: priceCentsSchema,
    costCents: optionalCostSchema,
    isAvailable: z.boolean().optional(),
    sortOrder: sortOrderSchema.optional(),
});
export const updateVariantSchema = createVariantSchema.partial();
/** Variante dentro del PATCH de un item: con `id` se actualiza, sin `id` se crea. */
export const itemVariantSyncSchema = createVariantSchema.extend({ id: idSchema.optional() });
// ---- Items ---------------------------------------------------------------------
export const createItemSchema = z.object({
    categoryId: idSchema,
    name: catalogNameSchema,
    description: optionalText(500),
    imageUrl: imageUrlSchema,
    priceCents: priceCentsSchema.default(0),
    costCents: optionalCostSchema,
    isAvailable: z.boolean().optional(),
    isActive: z.boolean().optional(),
    sortOrder: sortOrderSchema.optional(),
    tags: tagsSchema.optional(),
    variants: z.array(createVariantSchema).max(ITEM_VARIANTS_MAX, `Máximo ${ITEM_VARIANTS_MAX} variantes`).optional(),
});
/**
 * Si viene `variants`, reemplaza la lista completa: las que traen `id` se actualizan,
 * las nuevas se crean y las que faltan se borran.
 */
export const updateItemSchema = z.object({
    categoryId: idSchema.optional(),
    name: catalogNameSchema.optional(),
    description: optionalText(500),
    imageUrl: imageUrlSchema,
    priceCents: priceCentsSchema.optional(),
    costCents: optionalCostSchema,
    isAvailable: z.boolean().optional(),
    isActive: z.boolean().optional(),
    sortOrder: sortOrderSchema.optional(),
    tags: tagsSchema.optional(),
    variants: z
        .array(itemVariantSyncSchema)
        .max(ITEM_VARIANTS_MAX, `Máximo ${ITEM_VARIANTS_MAX} variantes`)
        .refine((vs) => {
        const ids = vs.map((v) => v.id).filter(Boolean);
        return new Set(ids).size === ids.length;
    }, 'Hay variantes repetidas')
        .optional(),
});
export const availabilitySchema = z.object({ isAvailable: z.boolean() });
// ---- Reordenar -----------------------------------------------------------------
export const reorderCatalogSchema = z
    .object({
    categories: z.array(z.object({ id: idSchema, sortOrder: sortOrderSchema })).max(500).optional(),
    items: z
        .array(z.object({ id: idSchema, categoryId: idSchema.optional(), sortOrder: sortOrderSchema }))
        .max(2000)
        .optional(),
})
    .refine((v) => (v.categories?.length ?? 0) + (v.items?.length ?? 0) > 0, 'No hay nada para reordenar')
    .refine((v) => {
    const ids = (v.categories ?? []).map((c) => c.id);
    return new Set(ids).size === ids.length;
}, 'Hay categorías repetidas')
    .refine((v) => {
    const ids = (v.items ?? []).map((i) => i.id);
    return new Set(ids).size === ids.length;
}, 'Hay items repetidos');
/** `?activeOnly=1` (o `true`). */
export const catalogQuerySchema = z.object({
    activeOnly: z
        .enum(['0', '1', 'true', 'false'])
        .optional()
        .transform((v) => v === '1' || v === 'true'),
});
//# sourceMappingURL=catalog.js.map